import TransactionButton from 'src/common/components/Buttons/TransactionButton'
import useCancelOffer from 'src/common/contracts/hooks/dotc/useCancelOffer'
import { Offer } from 'src/common/types/models/Offer'
import Button from 'src/common/ui-kit/Button/Button'
import Icon from 'src/common/ui-kit/Icon/Icon'
import {
  activeTabAtom,
  editOfferAtom,
  sharedOfferAtom,
} from 'src/pages/DiscoverPage/discover-store'
import clsx from 'clsx'
import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useOfferPermissions from './useOfferPermissions'
import { useNavigate } from 'react-router-dom'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  offersToTakeAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  verifyModalAtom,
} from 'src/pages/TradePage/stores/trade-store'
import Tooltip from 'src/common/ui-kit/Tooltip/Tooltip'
import { isBlockOffer, isFixedOffer } from 'src/common/utils/offers'
import useTier2 from 'src/common/queries/useTier2'
import { useActiveAccount } from 'thirdweb/react'
import { useConnectWallet } from 'src/common/hooks/web3/useConnectWallet'

interface OfferActionsProps {
  className?: string
  isAuthorized?: boolean
  authLoading?: boolean
  offer: Offer
  onCancel?: (offer: Offer) => void
}

const OfferActions: React.FC<OfferActionsProps> = ({
  offer,
  className,
  onCancel,
}) => {
  const navigate = useNavigate()

  const { t } = useTranslation('offersManagement', {
    keyPrefix: 'offerActions',
  })

  const activeTab = useAtomValue(activeTabAtom)
  const isMyTab = activeTab === 'my'

  const account = useActiveAccount()

  const setSharedOffer = useSetAtom(sharedOfferAtom)
  const setEditOffer = useSetAtom(editOfferAtom)
  const setVerifyModal = useSetAtom(verifyModalAtom)
  const setOfferToTake = useSetAtom(offersToTakeAtom)

  const { isOwner, canCancelOffer, canEditOffer } = useOfferPermissions(offer)

  const cancelOffer = useCancelOffer()
  const handleCancel = () => {
    onCancel?.(offer)
    return cancelOffer(offer.id)
  }

  const setSellToken = useSetAtom(sellTokenAtom)
  const setBuyToken = useSetAtom(buyTokenAtom)
  const setSellTokenAmount = useSetAtom(sellTokenAmountAtom)
  const setBuyTokenAmount = useSetAtom(buyTokenAmountAtom)

  const handleTakeOffer = () => {
    setSellToken(offer.withdrawalAsset)
    setBuyToken(offer.depositAsset)

    if (offer.amountOut) {
      const availableAmountOut =
        isBlockOffer(offer) && isFixedOffer(offer)
          ? offer.amountOut
          : offer.amountOut.div(offer.amountIn).mul(offer.availableAmount)

      setOfferToTake([{ ...offer, toTake: availableAmountOut }])

      setSellTokenAmount(availableAmountOut)
      setBuyTokenAmount(offer.availableAmount)
    }

    navigate(`/trade/take?offers=${offer.id}`)
    // window.open(`https://app.swarm.com/v1/onboarding?offer=${offer.id}`)
  }

  let buttonText = ''
  let buttonAction = () => {}
  const { connect } = useConnectWallet()
  const { data: isTier2 } = useTier2()
  if (!account) {
    buttonText = 'Connect'
    buttonAction = () => connect()
  } else if (!isTier2) {
    buttonText = t('getQualified')
    buttonAction = () => setVerifyModal(true)
  } else {
    buttonText = t('take')
    buttonAction = handleTakeOffer
  }

  return (
    <>
      <div
        className={clsx('flex justify-between items-center gap-1', className)}
      >
        <Button
          StartAdornment={<Icon color='primary' size={20} name='share' />}
          onClick={() => setSharedOffer(offer)}
          className='px-4 py-1.5'
          variant='outlined'
          size='sm'
        >
          {t('share')}
        </Button>
        <div className='flex flex-wrap gap-1 justify-end'>
          {!isMyTab && (
            <Tooltip
              position='left'
              content={isOwner ? t('ownerTooltip') : null}
            >
              <Button
                onClick={buttonAction}
                className='px-4 py-1.5'
                variant='outlined'
                size='sm'
              >
                {buttonText}
              </Button>
            </Tooltip>
          )}
          {isMyTab && (
            <Button
              disabled={!canEditOffer}
              className='px-4 py-1.5 w-32'
              variant='outlined'
              size='sm'
              onClick={() => setEditOffer({ offer, open: true })}
            >
              {t('edit')}
            </Button>
          )}
          {isMyTab && (
            <TransactionButton
              disabled={!canCancelOffer}
              transaction={handleCancel}
              className='px-4 py-1.5 w-32'
              variant='outlined'
              size='sm'
            >
              {t('cancel')}
            </TransactionButton>
          )}
        </div>
      </div>
    </>
  )
}

export default OfferActions
